<template>
  <div class="podium-container">
    <div class="podium-actions">
      <button class="share-btn" :disabled="isCapturing" @click="sharePodium">
        <i class="fa-solid fa-share-nodes"></i>
      </button>
    </div>
    <div ref="podiumCapture" class="podium-capture">
    <div class="top-3">
        <div
          v-for="p in topThree"
          :key="p.user_id"
          :class="['podium-card', `position-${p.final_position}`]"
          role="button"
          tabindex="0"
          @click="goToProfile(p.user_id)"
          @keyup.enter.prevent="goToProfile(p.user_id)"
        >
        <img :src="getAvatar(p.user)" @error="onImgError" class="avatar" alt="Athlete" />
        <div class="position">#{{ p.final_position }}</div>
        <div class="name">{{ p.user.name }} {{ p.user.surname }}</div>
        <div class="score">{{ formatScore(p.final_score) }}</div>
      </div>
    </div>

    <div class="fourth-row" v-if="fourth">
      <div
        class="podium-card fourth"
        role="button"
        tabindex="0"
        @click="goToProfile(fourth.user_id)"
        @keyup.enter.prevent="goToProfile(fourth.user_id)"
      >
        <div class="fourth-content">
          <span class="fourth-pos">#4</span>
          <img :src="getAvatar(fourth.user)" @error="onImgError" class="small-avatar fourth-avatar" alt="Athlete" />
          <div class="fourth-info">
            <div class="name">{{ fourth.user.name }} {{ fourth.user.surname }}</div>
            <div class="score">{{ formatScore(fourth.final_score) }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="secondary">
        <div
          v-for="p in nextFour"
          :key="p.user_id"
          class="podium-card secondary"
          role="button"
          tabindex="0"
          @click="goToProfile(p.user_id)"
          @keyup.enter.prevent="goToProfile(p.user_id)"
        >
        <img :src="getAvatar(p.user)" @error="onImgError" class="small-avatar" alt="Athlete" />
        <div class="secondary-position">#{{ p.final_position }}</div>
        <div class="name">{{ p.user.name }} {{ p.user.surname }}</div>
        <div class="score">{{ formatScore(p.final_score) }}</div>
      </div>
    </div>

    <div class="others">
      <h4 v-if="others.length">Ranking extendido</h4>
      <ul>
        <li
            v-for="p in others"
            :key="p.user_id"
            role="button"
            tabindex="0"
            @click="goToProfile(p.user_id)"
            @keyup.enter.prevent="goToProfile(p.user_id)"
        >
          #{{ p.final_position }} - {{ p.user.name }} {{ p.user.surname }} ({{ formatScore(p.final_score) }})
        </li>
      </ul>
    </div>
    </div>

    <!-- Contenedor invisible para capturas, sin imágenes remotas -->
    <div ref="podiumPrint" class="podium-capture print-capture" aria-hidden="true">
      <div class="top-3">
        <div
          v-for="p in topThree"
          :key="`print-${p.user_id}`"
          :class="['podium-card', `position-${p.final_position}`]"
        >
          <div class="initial-avatar">{{ getInitials(p.user) }}</div>
          <div class="position">#{{ p.final_position }}</div>
          <div class="name">{{ p.user.name }} {{ p.user.surname }}</div>
          <div class="score">{{ formatScore(p.final_score) }}</div>
        </div>
      </div>

      <div class="fourth-row" v-if="fourth">
        <div class="podium-card fourth">
          <div class="fourth-content">
            <span class="fourth-pos">#4</span>
            <div class="initial-avatar small-avatar">{{ getInitials(fourth.user) }}</div>
            <div class="fourth-info">
              <div class="name">{{ fourth.user.name }} {{ fourth.user.surname }}</div>
              <div class="score">{{ formatScore(fourth.final_score) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="secondary">
        <div
          v-for="p in nextFour"
          :key="`print-${p.user_id}`"
          class="podium-card secondary"
        >
          <div class="initial-avatar small-avatar">{{ getInitials(p.user) }}</div>
          <div class="secondary-position">#{{ p.final_position }}</div>
          <div class="name">{{ p.user.name }} {{ p.user.surname }}</div>
          <div class="score">{{ formatScore(p.final_score) }}</div>
        </div>
      </div>

      <div class="others">
        <h4 v-if="others.length">Ranking extendido</h4>
        <ul>
          <li v-for="p in others" :key="`print-${p.user_id}`">
            #{{ p.final_position }} - {{ p.user.name }} {{ p.user.surname }} ({{ formatScore(p.final_score) }})
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import router from "@/router/router";

export default {
  name: "CompetitionPodium",
  props: {
    podiums: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isCapturing: false,
      defaultAvatar: require('@/assets/user.png'),
      safeAvatar: null,
      safeAvatarReady: null,
      safeAvatarDataUri: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg==',
      captureMode: false,
    };
  },
  mounted() {
    this.prepareSafeAvatar();
  },
  computed: {
    sortedPodium() {
      return [...this.podiums].sort((a, b) => a.final_position - b.final_position);
    },
    topThree() {
      return this.sortedPodium.filter((p) => p.final_position <= 3);
    },
    fourth() {
      return this.sortedPodium.find((p) => p.final_position === 4);
    },
    nextFour() {
      return this.sortedPodium.filter((p) => p.final_position > 4 && p.final_position <= 8);
    },
    others() {
      return this.sortedPodium.filter((p) => p.final_position > 8);
    },
  },
  methods: {
    goToProfile(userId) {
      if (!userId) {
        return;
      }
      router.push({ path: `/profile/${userId}` });
    },
    formatScore(value) {
      const num = Number(value);
      return Number.isFinite(num) ? num.toFixed(2) : '0.00';
    },
    async sharePodium() {
      if (this.isCapturing) return;
      const el = this.$refs.podiumPrint || this.$refs.podiumCapture;
      if (!el) return;
      this.isCapturing = true;
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(el, {
          useCORS: false,
          backgroundColor: '#f5f5f5',
          scale: 2,
          allowTaint: true,
          logging: false,
          scrollX: 0,
          scrollY: -window.scrollY
        });

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
        if (!blob) return;
        const fileName = `podium-${Date.now()}.jpg`;
        if (navigator.canShare && navigator.canShare({ files: [new File([blob], fileName, { type: 'image/jpeg' })] })) {
          await navigator.share({ files: [new File([blob], fileName, { type: 'image/jpeg' })], title: 'Podium' });
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('sharePodium error', e);
      } finally {
        this.isCapturing = false;
      }
    },
    getAvatar(user = {}) {
      return user?.img_path || this.defaultAvatar;
    },
    getSafeAvatar() {
      return this.safeAvatar || this.safeAvatarDataUri;
    },
    async prepareSafeAvatar() {
      if (!this.safeAvatarReady) {
        this.safeAvatarReady = this.buildSafeAvatar();
      }
      return this.safeAvatarReady;
    },
    async ensureSafeAvatarReady() {
      try {
        await this.prepareSafeAvatar();
      } catch (e) {
        console.warn('ensureSafeAvatarReady failed', e);
      }
    },
    async buildSafeAvatar() {
      try {
        const res = await fetch(this.defaultAvatar);
        const blob = await res.blob();
        this.safeAvatar = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        console.warn('prepareSafeAvatar failed', e);
        this.safeAvatar = this.safeAvatarDataUri;
      }
    },
    getInitials(user = {}) {
      const name = (user.name || '').trim();
      const surname = (user.surname || '').trim();
      const first = name.charAt(0) || '';
      const last = surname.charAt(0) || '';
      return `${first}${last}`.toUpperCase() || 'A';
    },
    onImgError(evt) {
      if (evt && evt.target) {
        evt.target.src = this.defaultAvatar;
      }
    }
  }
};
</script>

<style scoped>
.podium-container {
  position: relative;
  margin-top: 30px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.5rem;
}
.podium-actions {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
}
.share-btn {
  border: 1px solid #2c7be5;
  background: #fff;
  color: #2c7be5;
  border-radius: 8px;
  padding: 6px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.share-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.podium-capture {
  width: 100%;
  padding: 44px 14px 28px;
  box-sizing: border-box;
  background: #f5f5f5;
  border-radius: 12px;
}
.print-capture {
  position: absolute;
  left: -9999px;
  top: 0;
  pointer-events: none;
  opacity: 1;
  display: block;
}
.initial-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d7e3ff, #f1f5ff);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #2c4460;
  margin-bottom: 0.5rem;
  border: 2px solid #fff;
  box-sizing: border-box;
}
.initial-avatar.small-avatar {
  width: 60px;
  height: 60px;
  margin: 0.35rem auto;
}
.top-3 {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.fourth-row {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
  margin-bottom: 1rem;
}
.podium-card {
  background: linear-gradient(to bottom, #fff, #eee);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  text-align: center;
  width: 140px;
  position: relative;
  transition: transform 0.3s;
  cursor: pointer;
}
.podium-card:hover {
  transform: scale(1.05);
}
.podium-card:focus-visible {
  outline: 2px solid #2c7be5;
  outline-offset: 3px;
}
.podium-card.position-1 {
  order: 2;
  background: rgba(255, 215, 0, 0.18);
  top: -30px;
}
.podium-card.position-2 {
  order: 1;
  background: rgba(192, 192, 192, 0.18);
}
.podium-card.position-3 {
  order: 3;
  background: rgba(205, 127, 50, 0.18);
}
.podium-card.fourth {
  background: #dfe2e6;
  opacity: 0.95;
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
  padding: 0.5rem 0.75rem;
}
.fourth-content {
  display: grid;
  grid-template-columns: 50px 60px 1fr;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
}
.fourth-pos {
  font-weight: 700;
  color: #2c7be5;
  font-size: 1rem;
}
.fourth-avatar {
  width: 60px;
  height: 60px;
  margin: 0;
}
.fourth-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
}
.fourth-info .name {
  margin: 0;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fourth-info .score {
  margin: 0;
}
.avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 0.5rem;
  border: 2px solid #fff;
}
.name {
  font-weight: bold;
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.score {
  font-size: 0.9rem;
  color: #222;
  border: 1px solid #2c7be5;
  border-radius: 12px;
  padding: 4px 10px;
  display: inline-block;
  background: #f5f9ff;
  min-width: 60px;
}
.secondary {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
}
.secondary .podium-card {
  width: 145px;
  background: #f7f7f7;
  opacity: 0.9;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.small-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0.5rem auto 0.35rem;
  object-fit: cover;
}
.secondary-position {
  font-weight: bold;
  margin-top: 0.35rem;
  margin-bottom: 0.35rem;
  display: block;
  width: 100%;
}
.secondary .name {
  margin: 0.2rem 0 0.2rem;
}
.secondary .score {
  margin-top: 0.2rem;
}
.others {
  text-align: center;
  max-width: 400px;
  margin: auto;
  font-size: 0.9rem;
  padding-top: 0.5rem;
}
.others ul {
  padding-left: 0;
  list-style: none;
}
.others li {
  cursor: pointer;
  padding: 0.25rem 0;
}
.others li:focus-visible {
  outline: 2px solid #2c7be5;
  outline-offset: 3px;
}
@media (max-width: 576px) {
  .podium-container {
    padding: 0.75rem;
    gap: 1.5rem;
  }
  .top-3 {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: end;
    gap: 0.6rem;
  }
  .podium-card {
    width: 100%;
    padding: 0.75rem;
  }
  .podium-card.position-1 {
    order: 2;
    top: -14px;
  }
  .podium-card.position-2 {
    order: 1;
    top: 0;
  }
  .podium-card.position-3 {
    order: 3;
    top: 0;
  }
  .avatar {
    width: 70px;
    height: 70px;
  }
  .secondary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.35rem;
  }
  .secondary .podium-card {
    width: 100%;
    padding: 0.5rem;
  }
  .secondary .small-avatar {
    width: 48px;
    height: 48px;
  }
  .fourth-row {
    margin-top: 0.35rem;
    margin-bottom: 0.75rem;
  }
  .podium-card.fourth {
    max-width: 100%;
    padding: 0.5rem 0.75rem;
  }
  .fourth-content {
    grid-template-columns: 40px 50px 1fr;
  }
  .fourth-avatar {
    width: 50px;
    height: 50px;
  }
}
</style>